import React from 'react';
import { isAfter } from 'date-fns';
import classNames from 'classnames';
import { useEnvironment, useTranslation, useExperiments } from '@wix/yoshi-flow-editor';
import { TPAComponentsConsumer, Spinner } from 'wix-ui-tpa';
import { Tooltip } from '@wix/design-system';
import { connect } from '../../../../common/components/runtime-context';
import { ArrowUpIcon } from '../ArrowUpIcon';
import { ArrowDownIcon } from '../ArrowDownIcon';
import { TextSubscriptionName } from '../TextSubscriptionName';
import {
  getDetails,
  getRegionalSettings,
  isDetailsLoading as isDetailsLoadingSelector,
  isDetailsOpen as isDetailsOpenSelector,
} from '../../selectors';
import { TextSubscriptionInfo } from '../TextSubscriptionInfo';
import { classes, st } from './SubscriptionsListItemBass.st.css';
import { BOOKINGS_APP_DEF_ID } from '../../constants';
import { PoolWithBalance, RootState } from '../../state';
import { CollectionMethod, Subscription, SubscriptionStatus } from '@wix/ambassador-billing-v1-subscription/types';
import { BenefitType } from '@wix/ambassador-pricing-plan-benefits-server/types';
import { DistributionStrategyType } from '@wix/ambassador-pricing-plans-plan-subscriptions-v1-plan-subscription/types';
import {
  hasScheduledSkip,
  isCurrentlySkipping,
  isPendingCancellationSubscription,
  isStoresSubscriptionOrLegacy,
} from '../../domainUtils';
import { formatDate } from '../../../../utils/displayUtils';
import SubscriptionPaymentsDetails from './SubscriptionPaymentsDetails';
import CallToActions from './CallToActions';
import { IActionCreators } from '../../../../types/internal-types';
import { getStatusTextTranslationKey, StatusBadgeBass } from '../StatusBadgeBass';
import CPDSectionHelper from './CPDSectionHelper';
import SubscriptionLineItemsBass from '../SubscriptionLineItemsBass/SubscriptionLineItemsBass';
import ExternalSubscriptionSectionHelper from './ExternalSubscriptionSectionHelper';
import SkipSectionHelper from './SkipSectionHelper';
import { Experiments } from '../../../../Experiments';
import {
  getSessionBenefitName,
  isCreditBasedBenefit,
  isSessionBasedBenefit,
} from '@wix/pricing-plans-utils/benefit-programs';
import { getBenefitsScheduler } from './benefitsSchedule';

type RuntimeProps = ReturnType<typeof mapRuntimeToProps>;

interface Props {
  subscription: Subscription;
}

const SubscriptionsListItemBass = ({
  subscription,
  isDetailsOpen,
  isDetailsLoading,
  actionCreators,
  details,
  regionalSettings,
}: Props & RuntimeProps) => {
  const { t } = useTranslation();
  const { isMobile } = useEnvironment();
  const { experiments } = useExperiments();
  const enableAdvancedPauseResume = experiments.enabled(Experiments.ADVANCED_PAUSE_RESUME);
  const showBenefitsScheduler = experiments.enabled(Experiments.SHOW_BENEFITS_SCHEDULER);
  const enableSkipSubscriptions = experiments.enabled(Experiments.SKIP_SUBSCRIPTIONS_MEMBERS_AREA);

  const endDate = subscription.endDate ?? undefined;
  const hasEndDate = Boolean(endDate);
  const validFrom = subscription.startDate ?? undefined;
  const isPaymentDateChanged = subscription.billingStatus?.updateBillingDateData;
  let trialEndDate: Date | undefined;
  let inTrial = false;

  if (subscription.billingSettings?.freeTrialDuration && subscription.startDate) {
    trialEndDate = subscription.billingStatus?.freeTrialData?.endDate ?? undefined;
    if (trialEndDate) {
      inTrial = subscription.status === SubscriptionStatus.ACTIVE && isAfter(trialEndDate, new Date());
    }
  }

  const isSubscriptionExternal = subscription.billingSettings?.collectionMethod === CollectionMethod.EXTERNAL;

  const getSubscriptionHeader = () => {
    const lines: string[] = [];
    const isStores = isStoresSubscriptionOrLegacy(subscription, enableSkipSubscriptions);
    if (subscription.description && isStores) {
      lines.push(subscription.description);
    }
    if (subscription.name) {
      lines.push(subscription.name);
    }
    return lines;
  };

  const getEndDate = (): string | null => {
    const cancellationDate = subscription.cancellationInfo?.cancellationDate ?? endDate; // endDate is sent as fallback due to backend bug in migrating of SAPI subscriptions.
    switch (subscription.status) {
      case SubscriptionStatus.PENDING:
        return t('app.title.starts', {
          date: formatDate(validFrom, regionalSettings),
          interpolation: { escapeValue: false },
        });
      case SubscriptionStatus.ACTIVE:
        if (isPendingCancellationSubscription(subscription)) {
          return t('app.title.expires', {
            date: formatDate(cancellationDate, regionalSettings),
            interpolation: { escapeValue: false },
          });
        }
        return hasEndDate
          ? t('app.title.expires', {
              date: formatDate(endDate, regionalSettings),
              interpolation: { escapeValue: false },
            })
          : t('app.title.valid-until-canceled');
      case SubscriptionStatus.PAUSED:
        return null;
      case SubscriptionStatus.ENDED:
        return t('app.title.expired', {
          date: formatDate(endDate, regionalSettings),
          interpolation: { escapeValue: false },
        });
      case SubscriptionStatus.CANCELED:
        return t('app.title.canceled', {
          date: formatDate(cancellationDate, regionalSettings),
          interpolation: { escapeValue: false },
        });
      default:
        return null;
    }
  };

  const getStartDate = (): string => {
    if (subscription.status === SubscriptionStatus.PENDING) {
      return hasEndDate
        ? t('app.details.expires', { date: formatDate(endDate, regionalSettings) })
        : t('app.details.valid-until-canceled');
    }
    return t('app.details.start-date', {
      date: formatDate(validFrom, regionalSettings),
      interpolation: { escapeValue: false },
    });
  };

  const getTrialEnds = (): string | null =>
    inTrial
      ? t('app.details.trial-ends', {
          date: formatDate(subscription.billingStatus?.freeTrialData?.endDate!, regionalSettings),
          interpolation: { escapeValue: false },
        })
      : null;

  const bookingsPools =
    details?.pools?.filter(({ pool }) =>
      pool.details?.benefits?.some((b) => b.providerAppId === BOOKINGS_APP_DEF_ID),
    ) ?? [];

  const getBenefitProgramsData = (): string[] => {
    if (!bookingsPools.length) {
      return [];
    }

    const isCreditBased = bookingsPools.length === 1 && isCreditBasedBenefit(bookingsPools[0].pool);

    if (isCreditBased) {
      const pool = bookingsPools[0].pool;
      const balance = bookingsPools[0].balance;

      const totalCredits = pool.details?.creditConfiguration?.amount;
      const remainingCredits = balance?.amount?.available;

      if (totalCredits === undefined || remainingCredits === undefined) {
        return [];
      }

      return [`${t('app.details.credits-remaining-header')} ${remainingCredits}/${totalCredits}`];
    }

    const isSessionBased = bookingsPools.every(({ pool }) => isSessionBasedBenefit(pool));
    if (isSessionBased) {
      const getRemainingText = ({ pool, balance }: PoolWithBalance) => {
        const isUnlimited = !pool.details?.creditConfiguration;
        return isUnlimited
          ? t('app.details.unlimited')
          : `${balance?.amount?.available ?? 0}/${pool.details?.creditConfiguration?.amount ?? 0}`;
      };

      if (bookingsPools.length === 1) {
        const poolWithBalance = bookingsPools[0];
        const name = getSessionBenefitName(poolWithBalance.pool) || poolWithBalance.pool.displayName;
        if (!name) {
          return [];
        }
        const remainingText = getRemainingText(poolWithBalance);
        if (name === subscription.name) {
          return [`${t('app.details.sessions-remaining-header')} ${remainingText}`];
        }
        return [t('app.details.sessions-remaining-header'), `${name}: ${remainingText}`];
      }

      const lines: string[] = [t('app.details.sessions-remaining-header')];

      for (const poolWithBalance of bookingsPools) {
        const name = getSessionBenefitName(poolWithBalance.pool) || poolWithBalance.pool.displayName;
        if (!name) {
          continue;
        }

        lines.push(`${name}: ${getRemainingText(poolWithBalance)}`);
      }

      return lines.length > 1 ? lines : [];
    }

    return [];
  };

  const getVerticalsData = () => {
    const lines: string[] = [];
    if (!bookingsPools.length && details?.benefitBalanceItems?.length) {
      const [bookingsItem] = details.benefitBalanceItems.filter(
        (item) => item.benefit?.appDefId === BOOKINGS_APP_DEF_ID,
      );

      if (bookingsItem?.benefit?.benefitType === BenefitType.LIMITED) {
        const { remainingCredits, totalCredits } = bookingsItem?.benefit;
        lines.push(t('app.details.sessions-remaining-without-total', { remainingCredits, totalCredits }));
      }
    }
    if (details?.storesSubscription) {
      const { lineItems, shippingInfo } = details.storesSubscription;
      if (lineItems?.length) {
        lines.push(t('app.details.quantity', { quantity: lineItems[0].quantity }));
      }
      if (shippingInfo?.shipmentDetails?.address) {
        const { address } = shippingInfo.shipmentDetails;
        type AddressType = typeof shippingInfo.shipmentDetails.address;
        const addressLines = ['addressLine1', 'addressLine2', 'city', 'zipCode', 'country']
          .map((field) => address[field as keyof AddressType])
          .filter(Boolean);

        if (addressLines.length) {
          lines.push(t('app.details.shipping-to'));
          addressLines.forEach((line) => lines.push(line as string));
        }
      }
    }
    return lines;
  };

  const SubscriptionHeader = getSubscriptionHeader();
  const EndDate = getEndDate();
  const StartDate = getStartDate();
  const TrialEnds = getTrialEnds();
  const VerticalsData = getVerticalsData();
  const BenefitProgramsData = getBenefitProgramsData();
  // One-time plan with non-renewing benefits has no recurring cycle, so hide the cycle rows.
  // PER_BILLING_CYCLE can't distinguish one-time from recurring alone, hence the totalCycles === 1 gate.
  const isOneTimeWithoutBenefitRenewal =
    subscription.billingSettings?.totalCycles === 1 &&
    details?.benefitDistribution?.settings?.distributionStrategyType === DistributionStrategyType.PER_BILLING_CYCLE;

  const BenefitsScheduleData: string[] =
    showBenefitsScheduler &&
    !isOneTimeWithoutBenefitRenewal &&
    bookingsPools.some(({ pool }) => !!pool.details?.creditConfiguration)
      ? getBenefitsScheduler({
          benefitDistribution: details?.benefitDistribution,
          regionalSettings,
          isPaused: subscription.status === SubscriptionStatus.PAUSED,
          t,
        })
      : [];

  return (
    <TPAComponentsConsumer>
      {({ mobile }) => (
        <div
          className={st(classes.root, {
            desktop: !isMobile,
            mobile: isMobile,
            skipSubscriptions: enableSkipSubscriptions,
          })}
          data-hook="subscription-list-item"
        >
          <div
            id={`subscription-title-${subscription.id}`}
            className={classes.titleContainer}
            data-hook="subscription-list-item-title"
            onClick={() =>
              isDetailsOpen
                ? actionCreators.closeDetails(subscription.id)
                : actionCreators.openDetails(subscription.id!)
            }
          >
            <div className={classes.lft}>
              <div className={classNames(classes.title, classes.columnA)}>
                {SubscriptionHeader.map((line, i) => (
                  <div key={i}>
                    <TextSubscriptionName>{line}</TextSubscriptionName>
                  </div>
                ))}
              </div>
              {EndDate && (
                <div className={classes.columnB} data-hook="subscription-date-info">
                  <TextSubscriptionName>{EndDate}</TextSubscriptionName>
                </div>
              )}
              {mobile && (
                <div className={classes.statusBadgeContainer}>
                  <StatusBadgeBass subscription={subscription} />
                </div>
              )}
            </div>
            <div className={classes.rgt}>
              {!mobile && (
                <div className={classes.tooltipWrapper}>
                  <Tooltip
                    content={t(
                      getStatusTextTranslationKey(subscription, { enableAdvancedPauseResume, enableSkipSubscriptions }),
                    )}
                    interactive
                    placement="top"
                  >
                    <div className={classes.statusBadgeContainer}>
                      <StatusBadgeBass subscription={subscription} showTooltip={false} />
                    </div>
                  </Tooltip>
                </div>
              )}
              <div className={classes.iconContainer}>
                <button
                  style={{ fontSize: 0 }}
                  aria-expanded={isDetailsOpen ? 'true' : 'false'}
                  aria-label="more details"
                  aria-describedby={`subscription-title-${subscription.id}`}
                >
                  {isDetailsOpen ? (
                    <ArrowUpIcon className={classes.arrow} />
                  ) : (
                    <ArrowDownIcon className={classes.arrow} />
                  )}
                </button>
              </div>
            </div>
          </div>

          {isDetailsOpen && (
            <div className={classes.details} data-hook="subscription-list-item-details">
              <div className={classes.contentContainer}>
                {isDetailsLoading && !details && (
                  <div className={classes.loader} data-hook="subscription-list-item-details-loader">
                    <Spinner />
                  </div>
                )}
                {details && (
                  <>
                    <div className={classes.columnA}>
                      <SubscriptionPaymentsDetails subscription={subscription} />
                    </div>
                    <div className={classes.columnB}>
                      <div>
                        <TextSubscriptionInfo>{StartDate}</TextSubscriptionInfo>
                      </div>
                      {TrialEnds && (
                        <div>
                          <TextSubscriptionInfo>{TrialEnds}</TextSubscriptionInfo>
                        </div>
                      )}
                      {VerticalsData.map((line, i) => (
                        <div key={i}>
                          <TextSubscriptionInfo>{line}</TextSubscriptionInfo>
                        </div>
                      ))}
                    </div>
                    {BenefitProgramsData.length > 0 && (
                      <div className={classes.columnC}>
                        {BenefitProgramsData.map((line, i) => (
                          <div key={i}>
                            <TextSubscriptionInfo>{line}</TextSubscriptionInfo>
                          </div>
                        ))}
                        {BenefitsScheduleData.map((line, i) => (
                          <div key={`schedule-${i}`} className={i === 0 ? classes.scheduleSection : undefined}>
                            <TextSubscriptionInfo>{line}</TextSubscriptionInfo>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
              {isPaymentDateChanged && (
                <div className={classes.sectionHelper}>
                  <CPDSectionHelper subscription={subscription} />
                </div>
              )}
              {isSubscriptionExternal && (
                <div className={classes.sectionHelper}>
                  <ExternalSubscriptionSectionHelper subscription={subscription} />
                </div>
              )}
              {enableSkipSubscriptions && (hasScheduledSkip(subscription) || isCurrentlySkipping(subscription)) && (
                <div className={classes.sectionHelper}>
                  <SkipSectionHelper subscription={subscription} />
                </div>
              )}
              {details && <CallToActions subscription={subscription} />}
              {details && (
                <SubscriptionLineItemsBass
                  subscription={subscription}
                  storesSubscription={details.storesSubscription}
                />
              )}
            </div>
          )}
        </div>
      )}
    </TPAComponentsConsumer>
  );
};

const mapRuntimeToProps = (state: RootState, { subscription }: Props, actionCreators: IActionCreators) => {
  const subscriptionId: string = subscription.id!;
  return {
    isDetailsOpen: isDetailsOpenSelector(state, subscriptionId),
    isDetailsLoading: isDetailsLoadingSelector(state, subscriptionId),
    actionCreators,
    details: getDetails(state, subscriptionId),
    regionalSettings: getRegionalSettings(state),
  };
};

export default connect(mapRuntimeToProps)(SubscriptionsListItemBass);
